import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  CopyField,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Spinner,
} from '@shared/ui';
import { generatePassword } from '@shared/lib/password';
import { isApiError } from '@shared/api';
import { ROUTES } from '@shared/config/routes';
import type {
  CreateProducerRequest,
  ManufacturerProfile,
  UpdateProfileRequest,
} from '@entities/manufacturer';
import { useCreateProducer } from '../model/mutations';
import { ProfileFields } from './profile-fields';
import { DocumentUploader } from './document-uploader';

// ---------- Schemas ----------

const credentialsSchema = z.object({
  email: z.string().email(),
  phone: z.string().max(32).optional().or(z.literal('')),
  companyName: z.string().min(1).max(256),
  password: z.string().min(8, 'Минимум 8 символов').max(128),
});
type CredentialsValues = z.infer<typeof credentialsSchema>;

const profileSchema = z.object({
  companyName: z.string().min(1).max(256),
  description: z.string().optional().or(z.literal('')),
  country: z.string().max(2).optional().or(z.literal('')),
  website: z.string().optional().or(z.literal('')),
  foundedYear: z
    .union([z.coerce.number().int().min(1800).max(new Date().getUTCFullYear() + 1), z.nan()])
    .optional(),
  employeesRange: z.string().optional().or(z.literal('')),
  ecoProducer: z.boolean().default(false),
});
type ProfileFormValues = z.infer<typeof profileSchema>;

type Step = 1 | 2 | 3;

// ---------- Wizard root ----------

export function OnboardingWizard({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [step, setStep] = useState<Step>(1);
  const [credentials, setCredentials] = useState<CredentialsValues | null>(null);
  const [profileDraft, setProfileDraft] = useState<ProfileFormValues | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [logoMediaId, setLogoMediaId] = useState<string | null>(null);
  const [videoMediaId, setVideoMediaId] = useState<string | null>(null);
  const [certificateMediaIds, setCertificateMediaIds] = useState<string[]>([]);
  const [ecoCertificateMediaIds, setEcoCertificateMediaIds] = useState<string[]>([]);
  const [created, setCreated] = useState<ManufacturerProfile | null>(null);
  const [emailConflict, setEmailConflict] = useState<string | null>(null);

  const create = useCreateProducer();

  const reset = () => {
    setStep(1);
    setCredentials(null);
    setProfileDraft(null);
    setCategories([]);
    setLogoMediaId(null);
    setVideoMediaId(null);
    setCertificateMediaIds([]);
    setEcoCertificateMediaIds([]);
    setCreated(null);
    setEmailConflict(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = () => {
    if (!credentials) return;
    const profile: UpdateProfileRequest | null =
      profileDraft || categories.length || logoMediaId || videoMediaId || certificateMediaIds.length || ecoCertificateMediaIds.length
        ? {
            companyName: profileDraft?.companyName || credentials.companyName,
            description: profileDraft?.description || null,
            country: profileDraft?.country || null,
            categories,
            logoMediaId,
            videoMediaId,
            certificateMediaIds,
            ecoCertificateMediaIds,
            ecoProducer: profileDraft?.ecoProducer ?? false,
            website: profileDraft?.website || null,
            foundedYear: Number.isFinite(profileDraft?.foundedYear as number)
              ? (profileDraft!.foundedYear as number)
              : null,
            employeesRange: profileDraft?.employeesRange || null,
          }
        : null;

    const body: CreateProducerRequest = {
      email: credentials.email,
      password: credentials.password,
      phone: credentials.phone || null,
      companyName: credentials.companyName,
      profile,
    };

    create.mutate(body, {
      onSuccess: setCreated,
      onError: (e) => {
        if (isApiError(e) && e.status === 409) {
          setEmailConflict('Email уже зарегистрирован');
          setStep(1);
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl">
        {created ? (
          <FinishModal profile={created} credentials={credentials!} onClose={close} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Новый производитель · Шаг {step} из 3</DialogTitle>
              <div className="mt-2 flex gap-1">
                {[1, 2, 3].map((s) => (
                  <span
                    key={s}
                    className={`h-1 flex-1 rounded ${s <= step ? 'bg-primary' : 'bg-muted'}`}
                  />
                ))}
              </div>
            </DialogHeader>

            {step === 1 ? (
              <Step1Credentials
                initial={credentials}
                emailConflict={emailConflict}
                onClearConflict={() => setEmailConflict(null)}
                onNext={(v) => {
                  setCredentials(v);
                  setStep(2);
                }}
                onCancel={close}
              />
            ) : null}

            {step === 2 ? (
              <Step2Profile
                defaultCompanyName={credentials?.companyName ?? ''}
                initial={profileDraft}
                categories={categories}
                onCategoriesChange={setCategories}
                onBack={() => setStep(1)}
                onNext={(v) => {
                  setProfileDraft(v);
                  setStep(3);
                }}
                onSkip={() => {
                  setProfileDraft(null);
                  setStep(3);
                }}
              />
            ) : null}

            {step === 3 ? (
              <Step3Documents
                logoMediaId={logoMediaId}
                onLogoChange={setLogoMediaId}
                videoMediaId={videoMediaId}
                onVideoChange={setVideoMediaId}
                certificateMediaIds={certificateMediaIds}
                onCertificatesChange={setCertificateMediaIds}
                ecoCertificateMediaIds={ecoCertificateMediaIds}
                onEcoCertificatesChange={setEcoCertificateMediaIds}
                onBack={() => setStep(2)}
                onFinish={submit}
                submitting={create.isPending}
              />
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Step 1 ----------

function Step1Credentials({
  initial,
  emailConflict,
  onClearConflict,
  onNext,
  onCancel,
}: {
  initial: CredentialsValues | null;
  emailConflict: string | null;
  onClearConflict: () => void;
  onNext: (v: CredentialsValues) => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: initial ?? {
      email: '',
      phone: '',
      companyName: '',
      password: generatePassword(),
    },
  });

  const email = watch('email');

  return (
    <form
      onSubmit={handleSubmit((v) => {
        onClearConflict();
        onNext(v);
      })}
      className="flex min-h-0 flex-1 flex-col gap-4"
    >
      <DialogBody className="space-y-3">
        <FormField label="Email *" htmlFor="email" error={emailConflict ?? errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="off"
            {...register('email', {
              onChange: () => (emailConflict ? onClearConflict() : undefined),
            })}
          />
        </FormField>
        <FormField label="Телефон" htmlFor="phone" error={errors.phone?.message}>
          <Input id="phone" {...register('phone')} placeholder="+998901234567" />
        </FormField>
        <FormField
          label="Название компании *"
          htmlFor="companyName"
          error={errors.companyName?.message}
        >
          <Input id="companyName" {...register('companyName')} />
        </FormField>

        <FormField
          label="Пароль *"
          htmlFor="password"
          error={errors.password?.message}
          hint="Минимум 8 символов. Передайте вендору вместе с email — подтверждение по почте не потребуется."
        >
          <div className="flex gap-2">
            <Input id="password" type="text" {...register('password')} className="font-mono" />
            <Button
              type="button"
              variant="outline"
              onClick={() => setValue('password', generatePassword(), { shouldValidate: true })}
              title="Сгенерировать"
            >
              🎲
            </Button>
          </div>
        </FormField>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={!email}>
          Далее
        </Button>
      </DialogFooter>
    </form>
  );
}

// ---------- Step 2 ----------

function Step2Profile({
  defaultCompanyName,
  initial,
  categories,
  onCategoriesChange,
  onBack,
  onNext,
  onSkip,
}: {
  defaultCompanyName: string;
  initial: ProfileFormValues | null;
  categories: string[];
  onCategoriesChange: (ids: string[]) => void;
  onBack: () => void;
  onNext: (v: ProfileFormValues) => void;
  onSkip: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues:
      initial ?? {
        companyName: defaultCompanyName,
        description: '',
        country: '',
        website: '',
        employeesRange: '',
        ecoProducer: false,
      },
  });

  return (
    <form onSubmit={handleSubmit(onNext)} className="flex min-h-0 flex-1 flex-col gap-4">
      <DialogBody className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Можно пропустить и заполнить профиль позже из карточки.
        </p>
        <ProfileFields
          register={register}
          errors={errors}
          selectedCategoryIds={categories}
          onCategoriesChange={onCategoriesChange}
        />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Назад
        </Button>
        <Button type="button" variant="ghost" onClick={onSkip}>
          Пропустить
        </Button>
        <Button type="submit">Далее</Button>
      </DialogFooter>
    </form>
  );
}

// ---------- Step 3 ----------

function Step3Documents({
  logoMediaId,
  onLogoChange,
  videoMediaId,
  onVideoChange,
  certificateMediaIds,
  onCertificatesChange,
  ecoCertificateMediaIds,
  onEcoCertificatesChange,
  onBack,
  onFinish,
  submitting,
}: {
  logoMediaId: string | null;
  onLogoChange: (id: string | null) => void;
  videoMediaId: string | null;
  onVideoChange: (id: string | null) => void;
  certificateMediaIds: string[];
  onCertificatesChange: (ids: string[]) => void;
  ecoCertificateMediaIds: string[];
  onEcoCertificatesChange: (ids: string[]) => void;
  onBack: () => void;
  onFinish: () => void;
  submitting: boolean;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <DialogBody className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Загрузите документы вендора. Всё можно добавить позже из карточки — шаг опциональный.
        </p>
        <DocumentUploader
          scope="PROFILE_LOGO"
          label="Логотип"
          multiple={false}
          mediaIds={logoMediaId ? [logoMediaId] : []}
          onChange={(ids) => onLogoChange(ids[0] ?? null)}
          accept="image/*"
        />
        <DocumentUploader
          scope="PRODUCT_VIDEO"
          label="Презентационное видео"
          multiple={false}
          mediaIds={videoMediaId ? [videoMediaId] : []}
          onChange={(ids) => onVideoChange(ids[0] ?? null)}
          accept="video/*"
        />
        <DocumentUploader
          scope="CERTIFICATE"
          label="Сертификаты / KYC документы"
          mediaIds={certificateMediaIds}
          onChange={onCertificatesChange}
        />
        <DocumentUploader
          scope="ECO_CERTIFICATE"
          label="Эко-сертификаты"
          mediaIds={ecoCertificateMediaIds}
          onChange={onEcoCertificatesChange}
        />
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onBack}>
          Назад
        </Button>
        <Button onClick={onFinish} disabled={submitting}>
          {submitting ? <Spinner /> : 'Создать вендора'}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ---------- Finish modal ----------

function FinishModal({
  profile,
  credentials,
  onClose,
}: {
  profile: ManufacturerProfile;
  credentials: CredentialsValues;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const vendorAppUrl = 'https://vendor.aidu24.com';

  const goToProfile = () => {
    navigate(ROUTES.manufacturerDetail(profile.id));
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>✅ Производитель создан</DialogTitle>
      </DialogHeader>
      <DialogBody className="space-y-3">
        <p className="text-sm text-muted-foreground">Передайте эти данные вендору:</p>
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <CopyField label="Ссылка" value={vendorAppUrl} mono={false} />
          <CopyField label="Email" value={credentials.email} />
          <CopyField label="Пароль" value={credentials.password} />
        </div>
        <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground/80">
          ⚠️ Пароль больше нигде не отобразится. Скопируйте его сейчас. Восстановить нельзя —
          только выдать новый.
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Закрыть
        </Button>
        <Button onClick={goToProfile}>К профилю →</Button>
      </DialogFooter>
    </>
  );
}
